import { createAsyncThunk } from '@reduxjs/toolkit';
import { serializeError } from '../../utils/errorHandling';
import {
  Checklist,
  Execution,
  ChecklistItem,
  ExecutionItem,
  Result,
  RowPosition,
  AddRowItems,
} from '../../interfaces/changeRequest';
import { ChangeRequestSource } from '../../enums/changeRequest';
import { makeApiCall } from '../../utils/makeApiCall';

// Get Change Request
export const getChangeRequest = createAsyncThunk(
  'changeRequest/get',
  async (id: string, { rejectWithValue }) => {
    try {
      return await makeApiCall({
        url: `/cr/${id}`,
        method: 'GET',
      });
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

type CreateChangeRequestPayload = {
  name?: string;
  summary?: string;
  checklist?: Checklist;
  execution?: Execution;
  source?: ChangeRequestSource;
};
// Create Change Request
export const createChangeRequest = createAsyncThunk(
  'changeRequest/create',
  async (payload: CreateChangeRequestPayload, { rejectWithValue }) => {
    try {
      const { name, summary, checklist, execution, source } = payload;
      return await makeApiCall({
        url: '/cr',
        method: 'POST',
        data: { name, summary, checklist, execution, source },
      });
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

// Clarify Change Request
export const clarifyChangeRequest = createAsyncThunk(
  'changeRequest/clarify',
  async (
    {
      id,
      name,
      summary,
      source = ChangeRequestSource.ANALYSIS_CHECKLIST,
    }: {
      id: string;
      name: string;
      summary: string;
      source: ChangeRequestSource;
    },
    { rejectWithValue }
  ) => {
    try {
      return await makeApiCall(
        {
          url: `/cr/clarify/${id}`,
          method: 'POST',
          data: { name, summary },
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

// Plan Change Request
export const planChangeRequest = createAsyncThunk(
  'changeRequest/plan',
  async (
    {
      id,
      name,
      summary,
      checklist,
      source = ChangeRequestSource.ACTIONS_EXECUTION,
    }: {
      id: string;
      name: string;
      summary: string;
      checklist: Checklist;
      source: ChangeRequestSource;
    },
    { rejectWithValue }
  ) => {
    try {
      return await makeApiCall(
        {
          url: `/cr/plan/${id}`,
          method: 'POST',
          data: { name, summary, checklist },
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const implementChangeRequestTask = createAsyncThunk(
  'changeRequest/task',
  async (
    {
      id,
      taskId,
      bodyData,
      source,
    }: {
      id: string;
      taskId: string;
      bodyData: Result;
      source: ChangeRequestSource;
    },
    { rejectWithValue }
  ) => {
    try {
      return await makeApiCall(
        {
          url: `/cr/implement/${id}/tasks/${taskId}?force=true`,
          method: 'POST',
          data: bodyData as unknown as Record<string, unknown>,
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

// Get Change Request Result
export const getChangeRequestResult = createAsyncThunk(
  'changeRequest/getResult',
  async (
    { croId, source }: { croId: string; source: ChangeRequestSource },
    { rejectWithValue }
  ) => {
    try {
      return await makeApiCall(
        {
          url: `/cr/results/${croId}`,
          method: 'GET',
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);
// TODO: Later on when adding more scenarios, we can create proper helper functions for each operation and updated item types
export const editChangeRequestItem = createAsyncThunk(
  'changeRequest/editItem',
  async (
    {
      path,
      updatedItem,
      crId,
      source,
    }: {
      path: string;
      updatedItem:
        | ChecklistItem
        | ExecutionItem
        | string
        | Checklist
        | Execution;
      crId: string;
      source: ChangeRequestSource;
    },
    { rejectWithValue }
  ) => {
    const body = {
      op: 'replace',
      path,
      value: updatedItem,
    };

    try {
      return await makeApiCall(
        {
          url: `/cr/${crId}`,
          method: 'PATCH',
          data: [body],
          headers: {
            'Content-Type': 'application/json-patch+json', // Override default content type
          },
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

/**
 * Delete row from change request and update the data
 * @param item - The item to delete
 */
export const deleteChangeRequestItem = createAsyncThunk(
  'changeRequest/deleteItem',
  async (
    {
      path,
      itemId,
      crId,
      source,
    }: {
      path: string;
      itemId: string;
      crId: string;
      source: ChangeRequestSource;
    },
    { rejectWithValue }
  ) => {
    const body = {
      op: 'remove',
      path,
    };

    try {
      return await makeApiCall(
        {
          url: `/cr/${crId}`,
          method: 'PATCH',
          data: [body],
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
        },
        source
      );
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);

/**
 * Add row to checklist or execution
 * @param updatedValues - The updated values
 */
export const addChangeRequestRow = createAsyncThunk(
  'changeRequest/addChangeRequestRow',
  async (
    {
      path,
      updatedValues,
      crId,
      source,
      direction,
      fromIndex,
      toIndex,
    }: {
      path: string;
      updatedValues: AddRowItems;
      crId: string;
      source: ChangeRequestSource;
      direction: RowPosition;
      fromIndex: number;
      toIndex: number;
    },
    { rejectWithValue }
  ) => {
    try {
      return {
        data: {
          success: true,
          updatedValues,
          direction,
          fromIndex,
          toIndex,
        },
        source,
      };
    } catch (error) {
      return rejectWithValue(serializeError(error));
    }
  }
);
